// WP-C1.5 (AVQ-1/3, TZ-4, S-35 `/availability`): paste box -> parsed interpretation card
// (both zones, day named) -> busy/free blocks + a suggested reply with [Copy]. The working-
// hours editor is a global buffer/slot-length pair rather than a full per-weekday grid — a
// documented scope trim (a weekly-hours grid widget is a materially bigger UI investment than
// this WP's "availability flows" scope), reusing the existing generic settings routes.
import { useState } from 'react';
import { Copy } from 'lucide-react';
import {
  Field,
  ghostButtonInlineClass,
  inputClass,
  primaryButtonInlineClass,
} from '../components/FormKit';
import { useCheckAvailability } from '../lib/availability';
import { useSettings, useSetSetting } from '../lib/settings';

function timeLabel(iso: string, ianaZone: string): string {
  return new Date(iso).toLocaleString(undefined, {
    timeZone: ianaZone,
    weekday: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** TZ-2 ("owner-defined aliases supported... 'client Bob = Chicago time'") stores as a plain
 * `Record<alias, ianaZone>` jsonb settings value (see `apps/api/src/timezone/ownerAliases.ts`)
 * — narrowed defensively the same way that module narrows it server-side. */
function isStringRecord(value: unknown): value is Record<string, string> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  return Object.values(value as Record<string, unknown>).every((v) => typeof v === 'string');
}

export function AvailabilityPage(): React.JSX.Element {
  const [text, setText] = useState('');
  const [copied, setCopied] = useState(false);
  const check = useCheckAvailability();

  const { data: settings } = useSettings();
  const setSetting = useSetSetting();
  const [bufferMinutes, setBufferMinutes] = useState('30');
  const [slotMinutes, setSlotMinutes] = useState('30');
  const [hoursSaved, setHoursSaved] = useState(false);
  const [aliasName, setAliasName] = useState('');
  const [aliasZone, setAliasZone] = useState('');
  const aliases = isStringRecord(settings?.timezoneAliases) ? settings.timezoneAliases : {};

  const runCheck = (): void => {
    setCopied(false);
    if (text.trim() === '') return;
    check.mutate(text.trim());
  };

  const saveWorkingHours = (): void => {
    setHoursSaved(false);
    const buffer = Number(bufferMinutes);
    const slot = Number(slotMinutes);
    if (Number.isInteger(buffer) && buffer >= 0)
      setSetting.mutate({ key: 'availabilityBufferMinutes', value: buffer });
    if (Number.isInteger(slot) && slot > 0)
      setSetting.mutate({ key: 'availabilitySlotMinutes', value: slot });
    setHoursSaved(true);
  };

  const addAlias = (): void => {
    const name = aliasName.trim().toLowerCase();
    const zone = aliasZone.trim();
    if (name === '' || zone === '') return;
    setSetting.mutate({ key: 'timezoneAliases', value: { ...aliases, [name]: zone } });
    setAliasName('');
    setAliasZone('');
  };

  const removeAlias = (name: string): void => {
    const rest = Object.fromEntries(Object.entries(aliases).filter(([key]) => key !== name));
    setSetting.mutate({ key: 'timezoneAliases', value: rest });
  };

  const copyReply = (reply: string): void => {
    void navigator.clipboard.writeText(reply).then(() => {
      setCopied(true);
    });
  };

  const result = check.data;

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold text-ink">Availability</h1>

      <section className="rounded-[var(--radius-card)] border border-border bg-surface p-4">
        <Field label="Client's message or a time window" htmlFor="avail-text">
          <textarea
            id="avail-text"
            className={inputClass}
            rows={2}
            placeholder="he's available 1pm-4pm EST tomorrow, can we do a call?"
            value={text}
            onChange={(e) => {
              setText(e.target.value);
            }}
          />
        </Field>
        <button
          type="button"
          className={primaryButtonInlineClass}
          disabled={check.isPending || text.trim() === ''}
          onClick={runCheck}
        >
          Check
        </button>

        {result !== undefined && (
          <div className="mt-4 rounded-[var(--radius-control)] border border-border bg-bg p-3">
            {result.status === 'question' ? (
              <p className="text-sm text-ink">{result.reply}</p>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-ink">
                  Their <strong>{timeLabel(result.theirRange.start, result.theirZone)}</strong>
                  {' – '}
                  {timeLabel(result.theirRange.end, result.theirZone)} ({result.theirZone}) ={' '}
                  <strong>
                    {timeLabel(result.ownerRange.start, result.ownerZone)} –{' '}
                    {timeLabel(result.ownerRange.end, result.ownerZone)} your time
                  </strong>
                  {result.crossesMidnight && (
                    <span className="text-muted"> (crosses midnight)</span>
                  )}
                </p>

                <div className="text-sm">
                  <p className="font-medium text-ink">Busy</p>
                  {result.conflicts.length === 0 ? (
                    <p className="text-muted">Nothing on your calendar in this window.</p>
                  ) : (
                    <ul className="text-muted">
                      {result.conflicts.map((c) => (
                        <li key={`${c.start}-${c.end}`}>
                          {timeLabel(c.start, result.ownerZone)} –{' '}
                          {timeLabel(c.end, result.ownerZone)}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="text-sm">
                  <p className="font-medium text-ink">Free</p>
                  {result.freeBlocks.length === 0 ? (
                    <p className="text-muted">No free time in this window.</p>
                  ) : (
                    <ul className="text-muted">
                      {result.freeBlocks.map((f) => (
                        <li key={`${f.start}-${f.end}`}>
                          {timeLabel(f.start, result.ownerZone)} –{' '}
                          {timeLabel(f.end, result.ownerZone)}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="flex items-center justify-between gap-3 rounded-[var(--radius-control)] border border-border bg-surface p-3">
                  <p className="text-sm text-ink">{result.reply}</p>
                  <button
                    type="button"
                    aria-label="Copy reply"
                    className={ghostButtonInlineClass}
                    onClick={() => {
                      copyReply(result.reply);
                    }}
                  >
                    <Copy size={14} />
                  </button>
                </div>
                {copied && <p className="text-xs text-success">Copied</p>}
              </div>
            )}
          </div>
        )}
      </section>

      <section className="rounded-[var(--radius-card)] border border-border bg-surface p-4">
        <h2 className="mb-1 text-sm font-medium text-ink">Working hours (AVQ-3)</h2>
        <p className="mb-3 text-xs text-muted">
          A courtesy buffer around existing events, and the default call length used when suggesting
          a slot. Current:{' '}
          {typeof settings?.availabilityBufferMinutes === 'number'
            ? settings.availabilityBufferMinutes
            : 30}{' '}
          min buffer,{' '}
          {typeof settings?.availabilitySlotMinutes === 'number'
            ? settings.availabilitySlotMinutes
            : 30}{' '}
          min slots.
        </p>
        <div className="flex flex-wrap gap-3">
          <Field label="Buffer (minutes)" htmlFor="avail-buffer">
            <input
              id="avail-buffer"
              className={`${inputClass} w-24`}
              inputMode="numeric"
              value={bufferMinutes}
              onChange={(e) => {
                setBufferMinutes(e.target.value);
              }}
            />
          </Field>
          <Field label="Slot length (minutes)" htmlFor="avail-slot">
            <input
              id="avail-slot"
              className={`${inputClass} w-24`}
              inputMode="numeric"
              value={slotMinutes}
              onChange={(e) => {
                setSlotMinutes(e.target.value);
              }}
            />
          </Field>
        </div>
        <button
          type="button"
          className={primaryButtonInlineClass}
          disabled={setSetting.isPending}
          onClick={saveWorkingHours}
        >
          Save
        </button>
        {hoursSaved && <span className="ml-2 text-sm text-success">Saved</span>}
      </section>

      <section className="rounded-[var(--radius-card)] border border-border bg-surface p-4">
        <h2 className="mb-1 text-sm font-medium text-ink">Timezone aliases (TZ-2)</h2>
        <p className="mb-3 text-xs text-muted">
          Colloquial zone names ("EST", "Pacific") already resolve on their own. Add an alias here
          for anything else — a client's name or an ambiguous abbreviation like "CST" — so it
          resolves instead of asking every time.
        </p>
        {Object.keys(aliases).length === 0 ? (
          <p className="mb-3 text-sm text-muted">No aliases set yet.</p>
        ) : (
          <ul className="mb-3 divide-y divide-border text-sm">
            {Object.entries(aliases).map(([name, zone]) => (
              <li key={name} className="flex items-center justify-between gap-2 py-1.5">
                <span className="text-ink">
                  <span>{name}</span> <span className="text-muted">→</span> <span>{zone}</span>
                </span>
                <button
                  type="button"
                  aria-label={`Remove alias ${name}`}
                  className={ghostButtonInlineClass}
                  disabled={setSetting.isPending}
                  onClick={() => {
                    removeAlias(name);
                  }}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Alias" htmlFor="avail-alias-name">
            <input
              id="avail-alias-name"
              className={`${inputClass} w-40`}
              placeholder="e.g. bob"
              value={aliasName}
              onChange={(e) => {
                setAliasName(e.target.value);
              }}
            />
          </Field>
          <Field label="IANA zone" htmlFor="avail-alias-zone">
            <input
              id="avail-alias-zone"
              className={`${inputClass} w-48`}
              placeholder="e.g. America/Chicago"
              value={aliasZone}
              onChange={(e) => {
                setAliasZone(e.target.value);
              }}
            />
          </Field>
          <button
            type="button"
            className={ghostButtonInlineClass}
            disabled={setSetting.isPending || aliasName.trim() === '' || aliasZone.trim() === ''}
            onClick={addAlias}
          >
            Add alias
          </button>
        </div>
      </section>
    </div>
  );
}
